import moment from "moment";

// Schedules of the Special Scheduled Price (ModalPriceSchedules), the promo code Usage Schedule
// (ModalPromoCodeSchedules) and the choice scheduled price (ModalChoicePriceSchedules). One
// schedule applies when all three hold, in Singapore time (GMT+8), as the API checks it:
// - dates: start_at (inclusive) .. end_at (exclusive), "YYYY-MM-DD HH:mm"; empty = no limit;
// - days: ISO weekdays [1..7] (1 = Monday); all seven = every day;
// - time: time_from .. time_to ("HH:mm") each day; both empty = all day; from later than to =
//   runs past midnight and belongs to the day it starts on.
export const PRICE_SCHEDULE_MAX = 20;
export const PRICE_SCHEDULE_FORMAT = "YYYY-MM-DD HH:mm";

export const SCHEDULE_DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];
export const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
export const WEEKDAYS = [1, 2, 3, 4, 5];
export const WEEKENDS = [6, 7];

const sameDays = (a, b) => a.length === b.length && a.every((day, index) => day === b[index]);

// A new schedule: no date limit, every day, all day.
export const newScheduleWhen = () => ({
  start_at: "",
  end_at: "",
  days: [...ALL_DAYS],
  time_from: "",
  time_to: "",
});

// The "when" of a schedule from the API (older rows have no days / times: every day, all day).
export const scheduleWhenFrom = (row) => ({
  start_at: row.start_at || "",
  end_at: row.end_at || "",
  days: Array.isArray(row.days) && row.days.length ? row.days.map(Number).sort((a, b) => a - b) : [...ALL_DAYS],
  time_from: row.time_from || "",
  time_to: row.time_to || "",
});

// What the API is sent for the "when" of a schedule.
export const scheduleWhenPayload = (row) => ({
  start_at: row.start_at || "",
  end_at: row.end_at || "",
  days: [...row.days].sort((a, b) => a - b),
  time_from: row.time_from || "",
  time_to: row.time_to || "",
});

// The window runs past midnight (22:00 - 02:00).
export const isOvernight = (row) => !!(row.time_from && row.time_to && row.time_to < row.time_from);

// "" or the first problem with the "when" of a schedule (same rules as the API).
export const scheduleWhenError = (row) => {
  if (row.start_at && row.end_at && row.end_at <= row.start_at) {
    return "The end date must be later than the start date.";
  }
  if (!row.days.length) {
    return "Pick at least one day.";
  }
  if (!!row.time_from !== !!row.time_to) {
    return "Enter both the start and end time, or choose All day.";
  }
  if (row.time_from && row.time_from === row.time_to) {
    return "The start and end time must differ.";
  }
  return "";
};

// No condition at all (any date, every day, all day). A price schedule like that would apply for
// good, so the price modals ask for dates, days or hours; a promo schedule may be open.
export const isUnrestrictedWhen = (row) =>
  !row.start_at && !row.end_at && sameDays([...row.days].sort((a, b) => a - b), ALL_DAYS) && !row.time_from && !row.time_to;

// "Mon–Wed, Fri" style list of the picked days.
const daysText = (days) => {
  const sorted = [...days].sort((a, b) => a - b);
  if (sameDays(sorted, ALL_DAYS)) {
    return "Every day";
  }
  if (sameDays(sorted, WEEKDAYS)) {
    return "Weekdays";
  }
  if (sameDays(sorted, WEEKENDS)) {
    return "Weekends";
  }
  const label = (day) => SCHEDULE_DAYS[day - 1].label;
  const runs = [];
  sorted.forEach((day) => {
    const last = runs[runs.length - 1];
    if (last && day === last[1] + 1) {
      last[1] = day;
    } else {
      runs.push([day, day]);
    }
  });
  return runs
    .map(([from, to]) => (from === to ? label(from) : `${label(from)}${to === from + 1 ? ", " : "–"}${label(to)}`))
    .join(", ");
};

const shownDate = (value) => moment(value, PRICE_SCHEDULE_FORMAT).format("DD/MM/YYYY HH:mm");

// One line saying when the schedule applies, e.g.
// "Weekends · 11:00–14:00 · 01/11/2026 00:00 – 30/11/2026 23:59".
export const scheduleWhenSummary = (row) => {
  if (!row.days.length) {
    return "No day picked";
  }
  const parts = [daysText(row.days)];
  if (row.time_from && row.time_to) {
    parts.push(`${row.time_from}–${row.time_to}${isOvernight(row) ? " (ends next day)" : ""}`);
  } else {
    parts.push("all day");
  }
  if (row.start_at && row.end_at) {
    parts.push(`${shownDate(row.start_at)} – ${shownDate(row.end_at)}`);
  } else if (row.start_at) {
    parts.push(`from ${shownDate(row.start_at)}`);
  } else if (row.end_at) {
    parts.push(`until ${shownDate(row.end_at)}`);
  } else {
    parts.push("no date limit");
  }
  return parts.join(" · ");
};

export const scheduleDayPresets = [
  { label: "Every day", days: ALL_DAYS },
  { label: "Weekdays", days: WEEKDAYS },
  { label: "Weekends", days: WEEKENDS },
];

export const isPreset = (days, preset) => sameDays([...days].sort((a, b) => a - b), preset);
